import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, Inject, PLATFORM_ID, OnInit, HostListener } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChatbotService } from '../../../services/chatbot.service';
import { QuestionBubble } from '../../../components/question-bubble/question-bubble';
import { ChatMarkdownPipe } from '../../../pipes/chat-markdown.pipe';
import { TrackClickDirective } from '../../../directives/track-click.directive';
import { runtimeLogger } from '../../../utils/runtime-logger.util';

@Component({
  selector: 'app-chatbot-bubble',
  standalone: true,
  imports: [CommonModule, HttpClientModule, QuestionBubble, ChatMarkdownPipe, TrackClickDirective],
  templateUrl: './chatbot-bubble.html',
  styleUrls: ['./chatbot-bubble.scss']
})
export class ChatbotBubble implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('messageInput') messageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('bubblesScrollContainer') bubblesScrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('bubblesTrack') bubblesTrack!: ElementRef<HTMLDivElement>;
  @ViewChild('chatMessages') chatMessages!: ElementRef<HTMLDivElement>;
  @ViewChild('chatWindow') chatWindow!: ElementRef<HTMLDivElement>;

  isChatOpen = true;
  messages: any[] = [];
  isLoading = false;
  sessionId: string; // Removida inicialização direta
  showQuestionBubbles = true;
  isChatPulsing = false;

  // Variáveis para redimensionamento
  isResizing = false;
  chatWidth = 380;
  chatHeight = 500;
  minWidth = 300;
  minHeight = 400;
  maxWidth = 800;
  maxHeight = 700;

  private startResizeX = 0;
  private startResizeY = 0;
  private startResizeWidth = 0;
  private startResizeHeight = 0;

  questionBubbles = [
    { text: 'ISR/Penetration atual' },
    { text: 'Vendas de SPF' },
    { text: 'Volume de contratos' },
    { text: 'Como aumentar a comissão?' },
  ];

  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;
  private autoScrollSpeed = 0.8;
  private animationId: number | null = null;
  private isBrowser: boolean;
  private typingAnimationTimeout: any;
  private pulseTimeout: any;

  constructor(
    private chatbotService: ChatbotService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Gerar um NOVO session ID usando o serviço
    this.sessionId = this.chatbotService.getNewSessionId();
    runtimeLogger.debug('Novo Session ID gerado:', this.sessionId);
  }

  ngOnInit() {
    this.showWelcomeMessage();
  }

  ngAfterViewInit() {
    if (!this.isBrowser) return;

    this.duplicateBubblesForLoop();
    this.setupDragScroll();
    this.startAutoScroll();
    this.scrollToBottom();
    this.startChatPulse();
  }

  // MÉTODOS DE REDIMENSIONAMENTO
  startResize(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    const rect = this.chatWindow.nativeElement.getBoundingClientRect();

    this.startResizeX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    this.startResizeY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.startResizeWidth = rect.width;
    this.startResizeHeight = rect.height;

    if (this.isBrowser) {
      document.addEventListener('mousemove', this.handleResize.bind(this));
      document.addEventListener('mouseup', this.stopResize.bind(this));
      document.addEventListener('touchmove', this.handleResize.bind(this));
      document.addEventListener('touchend', this.stopResize.bind(this));
    }
  }


  handleResize(event: MouseEvent | TouchEvent): void {
    if (!this.isResizing) return;

    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    const deltaX = this.startResizeX - clientX;
    const deltaY = this.startResizeY - clientY;

    const newWidth = Math.max(this.minWidth, Math.min(this.maxWidth, this.startResizeWidth + deltaX));
    const newHeight = Math.max(this.minHeight, Math.min(this.maxHeight, this.startResizeHeight + deltaY));

    this.chatWidth = newWidth;
    this.chatHeight = newHeight;
  }

  stopResize(): void {
    this.isResizing = false;

    if (this.isBrowser) {
      document.removeEventListener('mousemove', this.handleResize.bind(this));
      document.removeEventListener('mouseup', this.stopResize.bind(this));
      document.removeEventListener('touchmove', this.handleResize.bind(this));
      document.removeEventListener('touchend', this.stopResize.bind(this));
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onGlobalMouseMove(event: MouseEvent): void {
    if (this.isResizing) {
      this.handleResize(event);
    }
  }

  @HostListener('document:mouseup')
  onGlobalMouseUp(): void {
    if (this.isResizing) {
      this.stopResize();
    }
  }

  @HostListener('document:touchmove', ['$event'])
  onGlobalTouchMove(event: TouchEvent): void {
    if (this.isResizing) {
      this.handleResize(event);
    }
  }

  @HostListener('document:touchend')
  onGlobalTouchEnd(): void {
    if (this.isResizing) {
      this.stopResize();
    }
  }

  private startChatPulse() {
    if (!this.isBrowser) return;

    let pulseCount = 0;
    const maxPulses = 3;

    const doPulse = () => {
      this.isChatPulsing = true;

      setTimeout(() => {
        this.isChatPulsing = false;
        pulseCount++;

        if (pulseCount < maxPulses) {
          setTimeout(() => {
            doPulse();
          }, 400);
        }
      }, 300);
    };

    setTimeout(() => {
      doPulse();
    }, 1000);
  }

  private showWelcomeMessage() {
    const welcomeText = 'Olá! Eu sou o assistente virtual da Maia. Como posso ajudar você hoje?';
    let currentText = '';
    let index = 0;

    this.addTypingMessage();

    const typeWriter = () => {
      if (index < welcomeText.length) {
        currentText += welcomeText.charAt(index);
        this.messages[0] = {
          text: currentText,
          isUser: false,
          timestamp: new Date(),
          isTyping: false
        };
        index++;
        this.typingAnimationTimeout = setTimeout(typeWriter, 50);
      } else {
        this.scrollToBottom();
      }
    };

    this.messages = [{
      text: '',
      isUser: false,
      timestamp: new Date(),
      isTyping: false
    }];

    setTimeout(() => {
      typeWriter();
    }, 1000);
  }

  private scrollToBottom(): void {
    if (!this.isBrowser || !this.chatMessages?.nativeElement) return;

    setTimeout(() => {
      const messagesContainer = this.chatMessages.nativeElement;
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }, 100);
  }

  ngOnDestroy() {
    this.stopAutoScroll();
    if (this.typingAnimationTimeout) {
      clearTimeout(this.typingAnimationTimeout);
    }
    if (this.pulseTimeout) {
      clearTimeout(this.pulseTimeout);
    }
    this.stopResize();
  }

  private duplicateBubblesForLoop() {
    if (!this.isBrowser) return;

    const track = this.bubblesTrack?.nativeElement;
    if (track) {
      setTimeout(() => {
        track.style.width = 'max-content';
      }, 100);
    }
  }

  private setupDragScroll() {
    if (!this.isBrowser) return;

    const container = this.bubblesScrollContainer?.nativeElement;
    if (!container) return;

    container.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));

    container.addEventListener('touchstart', this.onTouchStart.bind(this));
    document.addEventListener('touchmove', this.onTouchMove.bind(this));
    document.addEventListener('touchend', this.onTouchEnd.bind(this));

    container.addEventListener('selectstart', (e) => e.preventDefault());
  }

  private onMouseDown(e: MouseEvent) {
    this.isDragging = true;
    this.startX = e.pageX - this.bubblesScrollContainer.nativeElement.offsetLeft;
    this.scrollLeft = this.bubblesScrollContainer.nativeElement.scrollLeft;
    this.bubblesScrollContainer.nativeElement.style.cursor = 'grabbing';
    this.bubblesScrollContainer.nativeElement.style.scrollBehavior = 'auto';
    this.stopAutoScroll();
    e.preventDefault();
  }

  private onMouseMove(e: MouseEvent) {
    if (!this.isDragging) return;

    const x = e.pageX - this.bubblesScrollContainer.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    this.bubblesScrollContainer.nativeElement.scrollLeft = this.scrollLeft - walk;
  }

  private onMouseUp() {
    this.isDragging = false;
    this.bubblesScrollContainer.nativeElement.style.cursor = 'grab';
    this.bubblesScrollContainer.nativeElement.style.scrollBehavior = 'smooth';
    this.startAutoScroll();
  }

  private onTouchStart(e: TouchEvent) {
    this.isDragging = true;
    this.startX = e.touches[0].pageX - this.bubblesScrollContainer.nativeElement.offsetLeft;
    this.scrollLeft = this.bubblesScrollContainer.nativeElement.scrollLeft;
    this.stopAutoScroll();
    e.preventDefault();
  }

  private onTouchMove(e: TouchEvent) {
    if (!this.isDragging) return;

    const x = e.touches[0].pageX - this.bubblesScrollContainer.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    this.bubblesScrollContainer.nativeElement.scrollLeft = this.scrollLeft - walk;
  }

  private onTouchEnd() {
    this.isDragging = false;
    this.startAutoScroll();
  }

  private startAutoScroll() {
    if (!this.isBrowser) return;

    this.stopAutoScroll();

    const scroll = () => {
      if (this.isDragging || !this.showQuestionBubbles || !this.isChatOpen) {
        this.animationId = requestAnimationFrame(scroll);
        return;
      }

      const container = this.bubblesScrollContainer?.nativeElement;
      const track = this.bubblesTrack?.nativeElement;

      if (container && track) {
        container.scrollLeft += this.autoScrollSpeed;

        const scrollWidth = track.scrollWidth / 2;
        if (container.scrollLeft >= scrollWidth) {
          container.scrollLeft = 0;
        }
      }

      this.animationId = requestAnimationFrame(scroll);
    };

    this.animationId = requestAnimationFrame(scroll);
  }

  private stopAutoScroll() {
    if (this.animationId && this.isBrowser) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;

    if (this.isChatOpen && this.isBrowser) {
      setTimeout(() => {
        this.messageInput.nativeElement.focus();
        this.startAutoScroll();
        this.scrollToBottom();
        this.startChatPulse();
      }, 400);
    } else {
      this.stopAutoScroll();
    }
  }

  closeChat(): void {
    this.isChatOpen = false;
    this.stopAutoScroll();
  }

  sendMessage(): void {
    const messageText = this.messageInput.nativeElement.value.trim();

    if (!messageText) return;

    this.addUserMessage(messageText);
    this.messageInput.nativeElement.value = '';
    this.isLoading = true;
    this.hideQuestionBubbles();

    this.addTypingMessage();

    this.chatbotService.sendMessage(messageText, this.sessionId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.removeTypingMessage();
        this.addBotMessage(response.response);
        this.showQuestionBubblesAfterDelay();
        this.scrollToBottom();
      },
      error: (error) => {
        this.isLoading = false;
        this.removeTypingMessage();
        this.addBotMessage('Desculpe, ocorreu um erro. Tente novamente.');
        this.showQuestionBubblesAfterDelay();
        this.scrollToBottom();
      }
    });
  }

  sendQuickQuestion(question: string): void {
    this.addUserMessage(question);
    this.isLoading = true;
    this.hideQuestionBubbles();

    this.addTypingMessage();

    this.chatbotService.sendMessage(question, this.sessionId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.removeTypingMessage();
        this.addBotMessage(response.response);
        this.showQuestionBubblesAfterDelay();
        this.scrollToBottom();
      },
      error: (error) => {
        this.isLoading = false;
        this.removeTypingMessage();
        this.addBotMessage('Desculpe, ocorreu um erro. Tente novamente.');
        this.showQuestionBubblesAfterDelay();
        this.scrollToBottom();
      }
    });
  }

  hideQuestionBubbles(): void {
    this.showQuestionBubbles = false;
    this.stopAutoScroll();
  }

  showQuestionBubblesAfterDelay(): void {
    setTimeout(() => {
      this.showQuestionBubbles = true;
      if (this.isBrowser) {
        this.startAutoScroll();
      }
    }, 1000);
  }

  getCurrentTime(): string {
    return new Date().toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private addUserMessage(text: string): void {
    this.messages.push({
      text: text,
      isUser: true,
      timestamp: new Date(),
      isTyping: false
    });
    this.scrollToBottom();
  }

  private addBotMessage(text: string): void {
    this.messages.push({
      text: text,
      isUser: false,
      timestamp: new Date(),
      isTyping: false
    });
    this.scrollToBottom();
  }

  private addTypingMessage(): void {
    this.messages.push({
      text: '...',
      isUser: false,
      timestamp: new Date(),
      isTyping: true
    });
    this.scrollToBottom();
  }

  private removeTypingMessage(): void {
    const lastMessage = this.messages[this.messages.length - 1];
    if (lastMessage && lastMessage.isTyping) {
      this.messages.pop();
    }
  }

  // Novo método para reiniciar conversa
  restartConversation(): void {
    // Gerar um novo session ID
    this.sessionId = this.chatbotService.getNewSessionId();
    runtimeLogger.debug('Conversa reiniciada com novo Session ID:', this.sessionId);

    // Limpar mensagens
    this.messages = [];

    // Mostrar mensagem de boas-vindas novamente
    this.showWelcomeMessage();

    // Mostrar bubbles
    this.showQuestionBubbles = true;
    this.startAutoScroll();
  }
}
